import { ErrorMessage } from "@atlaskit/form";
import { currencyFormatter } from "@fla/common";
import {
  AssetType,
  Button,
  ConsentOrdersPathName,
  CustomCheckbox,
  FormNavigation,
  VehicleBaseProps,
  VehicleBaseSchema,
  Vehicle_InitialState,
  useNames,
  useVehicle
} from "@fla/components";
import VehicleIcon from "@fla/components/src/assets/svgs/vehicle.svg";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppDispatch } from "app/store";
import { withConsentOrder } from "components/HOCs/withConsentOrder";
import { useConsentOrders } from "hooks/ConsentOrders";
import { SliceStatus } from "interfaces";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { selectAsset, selectBaseAssetsDebts, selectConsentOrdersBase } from "../../../consentOrdersSelectors";
import { consentOrdersActions } from "../../../consentOrdersSlice";

const defaultValues = {
  if_no_vehicle: "no"
};
function Vehicle() {
  const { initialDetails } = useSelector(selectConsentOrdersBase);
  const vehicleData = useSelector(selectAsset(AssetType.VEHICLE)) as Vehicle_InitialState[];
  const _useNames = useNames({ initialDetails });
  const { spouseFirstName } = _useNames;
  const assetsDebts = useSelector(selectBaseAssetsDebts);
  const [status, setStatus] = useState(SliceStatus.Idle);
  const [error, setError] = useState<string>();

  const location = useLocation();
  const navigate = useNavigate();
  const { control, handleSubmit, reset, getValues, watch } = useForm({
    mode: "all",
    defaultValues,
    resolver: yupResolver(VehicleBaseSchema)
  });

  const handleBackClick = () => {
    navigate(ConsentOrdersPathName.Bank);
  };

  const editData = (values?: VehicleBaseProps) => {
    navigate(`${ConsentOrdersPathName.Vehicle}/add`, {
      state: {
        parent: getValues(),
        child: values
      }
    });
  };

  const dispatch = useAppDispatch();
  const { updateAssetsDebts } = useConsentOrders();

  const onSubmit = async (values: VehicleBaseProps) => {
    try {
      setStatus(SliceStatus.Pending);

      if (values.if_no_vehicle === "yes" && vehicleData.length) {
        setError("If neither of you have any vehicles in Australia, then please remove the added vehicles details.");
      } else if (values.if_no_vehicle !== "yes" && !vehicleData.length) {
        setError("please add at least one vehicle");
      } else {
        setError("");
        const payload = {
          vehicle: { data: values, isCompleted: true },
          currentStep: 5
        };
        await updateAssetsDebts(payload);
        dispatch(consentOrdersActions.setAssetsDebts(payload));
        navigate(ConsentOrdersPathName.Investments);
      }
      setStatus(SliceStatus.Resolved);
    } catch (error) {
      setStatus(SliceStatus.Rejected);
    }
  };

  useEffect(() => {
    if (location.state) {
      reset(location.state);
    } else {
      reset(assetsDebts?.vehicle?.data);
    }
  }, [location]);

  const { ownCard, splitCard, userTotalAmount, spouseTotalAmount } = useVehicle({
    vehicleData,
    ..._useNames
  });

  return (
    <section className="w-full py-10">
      <form
        id="vehicle-form"
        className="md:w-10/12 lg:max-w-2xl mx-auto h-fit px-5 md:px-10 md:py-5 space-y-5"
        onSubmit={handleSubmit(onSubmit)}
      >
        <img src={VehicleIcon} className="w-10 h-auto mx-auto my-3" alt="superannuation" />

        <h3 className="text-center my-5">Motor Vehicles</h3>

        <div className="w-full py-10 border border-gray-400 border-dashed rounded-lg flex flex-col items-center justify-center space-y-10 mb-5">
          <p className="px-16 text-center text-base font-bold">
            Please add all the motor vehicles you and {spouseFirstName} have in Australia
          </p>
          <Button
            className="px-5 py-2 uppercase"
            fontSize="sm"
            onClick={() => editData(undefined)}
            disabled={watch("if_no_vehicle") === "yes"}
          >
            + Add Motor Vehicle
          </Button>
        </div>

        <article className="mb-12">
          <CustomCheckbox
            control={control}
            name="if_no_vehicle"
            title="Click if neither of you own a motor vehicle in Australia "
            valueType="string"
            classes="w-fit mx-auto"
            hideErrorMsg
            onChange={() => setError("")}
          />
          {error ? (
            <div className="max-w-sm mx-auto text-center">
              <ErrorMessage>{error}</ErrorMessage>
            </div>
          ) : null}
        </article>

        {Array.isArray(vehicleData) && vehicleData.length ? (
          <>
            <p className="uppercase text-gray-500 text-xl font-medium my-5">You’re Keeping</p>

            {ownCard("user", editData)}
            {splitCard("user", editData)}
            <section className="flex justify-end items-center mb-12">
              <p className="text-gray-600">
                Total:
                <span className="text-2xl ml-2">{currencyFormatter.format(userTotalAmount)}</span>
              </p>
            </section>

            <p className="uppercase text-gray-500 text-xl font-medium my-5">{spouseFirstName} Keeping</p>

            {ownCard("spouse", editData)}
            {splitCard("spouse", editData)}
            <section className="flex justify-end items-center mb-12">
              <p className="text-gray-600">
                Total:
                <span className="text-2xl ml-2">{currencyFormatter.format(spouseTotalAmount)}</span>
              </p>
            </section>
          </>
        ) : null}

        <FormNavigation
          isFirst={false}
          isLast={false}
          form="vehicle-form"
          handleBackClick={handleBackClick}
          nextButtonText="Continue"
          isSubmitting={status === SliceStatus.Pending}
          classes="mt-12"
        />
      </form>
    </section>
  );
}
export default withConsentOrder(Vehicle);
